import { useForm, FormProvider } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Stack } from '@mui/material'
import {
  ObservationLogFormData,
  ObservationLogFormActions,
} from '@/components/forms'
import {
  ControlledDateTimeField,
  ControlledMediaUpload,
  ControlledSelect,
  ControlledTextField,
} from '@/components/inputs'
import {
  FISH_SPECIE_OPTIONS,
  OTHER_SPECIE_OPTION_VALUE,
  FISH_COUNTING_METHOD_OPTIONS,
} from '../fieldsOptions'
import { useAuth } from '@/hooks'
import { yupResolver } from '@hookform/resolvers/yup'
import { useApiErrorData } from '@/hooks/forms/useApiErrorData'
import { FormComponentProps, UserGroups } from '@/types'
import { schema } from './validationSchema'

export const FishForm = ({
  onSubmit,
  defaultValues,
  isLoading,
  apiErrorData,
}: FormComponentProps<ObservationLogFormData>): JSX.Element => {
  const { t } = useTranslation('observationLogs')

  const { hasPermission } = useAuth()

  const methods = useForm<ObservationLogFormData>({
    mode: 'onChange',
    defaultValues: {
      specie: '',
      other_specie: '',
      amount: '',
      counting_method: '',
      behaviour_notes: '',
      observed_at: '',
      photos: undefined,
    },
    values: defaultValues,
    resolver: yupResolver(schema),
  })

  useApiErrorData<ObservationLogFormData>(apiErrorData, methods)

  const watchedSpecie = methods.watch('specie')

  const disabledForTheUser = !hasPermission([
    UserGroups.MANAGER,
    UserGroups.EMPLOYEE,
    UserGroups.DATA_CONTROLLER,
  ])

  return (
    <FormProvider {...methods}>
      <Stack mt={4} width="100%" alignItems="stretch" spacing={4}>
        <Stack direction="row" spacing={2} justifyContent="flex-start">
          <ControlledSelect
            control={methods.control}
            name="specie"
            label={t('observation-log.fieldLabels.specie')}
            options={FISH_SPECIE_OPTIONS}
          />
          {watchedSpecie === OTHER_SPECIE_OPTION_VALUE && (
            <>
              <ControlledTextField
                control={methods.control}
                name="other_specie"
                placeholder={t('observation-log.fieldLabels.otherSpecie')}
                disabled={disabledForTheUser}
              />
            </>
          )}
          <ControlledTextField
            control={methods.control}
            name="amount"
            placeholder={t('observation-log.fieldLabels.amount')}
            disabled={disabledForTheUser}
          />
          <ControlledSelect
            control={methods.control}
            name="counting_method"
            label={t('observation-log.fieldLabels.countingMethod')}
            options={FISH_COUNTING_METHOD_OPTIONS}
            disabled={disabledForTheUser}
          />
        </Stack>
        <ControlledTextField
          control={methods.control}
          name="behaviour_notes"
          label={t('observation-log.fieldLabels.behaviourNotes')}
          multiline={true}
          rows={3}
          inputProps={{
            style: {
              padding: 0,
            },
          }}
          disabled={disabledForTheUser}
        />

        <ControlledDateTimeField
          name="observed_at"
          control={methods.control}
          label={t('observation-log.fieldLabels.observedAt')}
        />

        <ControlledMediaUpload control={methods.control} name="photos" />

        <ObservationLogFormActions
          onSubmit={onSubmit}
          defaultValues={defaultValues}
          isLoading={isLoading}
          methods={methods}
        />
      </Stack>
    </FormProvider>
  )
}
